import { asArray, asRecord } from "@/lib/type-utils";

type UnknownRecord = Record<string, unknown>;

export type TwitterSentiment = "Bullish" | "Bearish" | "Neutral";

export interface TwitterTweet {
  id: string;
  text: string;
  author: string;
  username: string;
  author_image: string;
  verified: boolean;
  influencer: boolean;
  likes: number;
  retweets: number;
  followers: number;
  date: string;
  sentiment: TwitterSentiment;
  engagement: number;
  hashtags: string[];
  trending: boolean;
}

export interface TwitterHashtag {
  tag: string;
  count: number;
}

export interface TwitterInfluencer {
  name: string;
  username: string;
  handle: string;
  followers: number;
  verified: boolean;
}

export interface TwitterFeedSummary {
  positive: number;
  negative: number;
  neutral: number;
  total: number;
  hashtags: TwitterHashtag[];
  influencers: TwitterInfluencer[];
}

const POSITIVE_TERMS = [
  "bullish",
  "surge",
  "moon",
  "breakout",
  "winning",
  "gains",
  "approve",
  "approved",
  "upside",
  "buy",
];

const NEGATIVE_TERMS = [
  "bearish",
  "dump",
  "crash",
  "selloff",
  "lose",
  "loss",
  "deny",
  "downside",
];

function toNumber(value: unknown): number {
  const num = Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
}

function normalizeHashtag(value: string): string {
  return value.replace(/^#/, "").trim().toLowerCase();
}

function classifySentiment(text: string): TwitterSentiment {
  const lower = text.toLowerCase();
  const positive = POSITIVE_TERMS.reduce((score, term) => {
    const regex = new RegExp(`\\b${term}\\b`);
    return score + (regex.test(lower) ? 1 : 0);
  }, 0);
  const negative = NEGATIVE_TERMS.reduce((score, term) => {
    const regex = new RegExp(`\\b${term}\\b`);
    return score + (regex.test(lower) ? 1 : 0);
  }, 0);

  if (positive > negative) {
    return "Bullish";
  }
  if (negative > positive) {
    return "Bearish";
  }
  return "Neutral";
}

function getTimelineInstructions(payload: unknown): UnknownRecord[] {
  const root = asRecord(payload);
  const candidates = [
    asRecord(asRecord(root.result).timeline),
    asRecord(asRecord(asRecord(root.raw_results).result).timeline),
    asRecord(asRecord(asRecord(root.results).result).timeline),
    asRecord(root.timeline),
  ];

  for (const timeline of candidates) {
    const instructions = asArray(timeline.instructions);
    if (instructions.length > 0) {
      return instructions;
    }
  }

  return [];
}

function collectTweetResults(node: unknown, acc: UnknownRecord[]): void {
  if (Array.isArray(node)) {
    for (const item of node) {
      collectTweetResults(item, acc);
    }
    return;
  }

  const record = asRecord(node);
  if (Object.keys(record).length === 0) {
    return;
  }

  const tweetResult = asRecord(asRecord(record.tweet_results).result);
  if (Object.keys(tweetResult).length > 0) {
    acc.push(tweetResult);
  }

  for (const [key, value] of Object.entries(record)) {
    if (key === "tweet_results") {
      continue;
    }
    if (value && typeof value === "object") {
      collectTweetResults(value, acc);
    }
  }
}

function extractHashtags(legacy: UnknownRecord, text: string): string[] {
  const tags = new Set<string>();

  for (const hashtag of asArray(asRecord(legacy.entities).hashtags)) {
    const tag = normalizeHashtag(String(hashtag.text ?? ""));
    if (tag) {
      tags.add(tag);
    }
  }

  for (const match of text.matchAll(/#([a-z0-9_]+)/gi)) {
    const tag = normalizeHashtag(match[1] ?? "");
    if (tag) {
      tags.add(tag);
    }
  }

  return [...tags];
}

function normalizeTweet(tweet: UnknownRecord): TwitterTweet | null {
  const legacy = asRecord(tweet.legacy);
  const user = asRecord(asRecord(asRecord(tweet.core).user_results).result);
  const userCore = asRecord(user.core);
  const userLegacy = asRecord(user.legacy);

  const text = String(legacy.full_text ?? "").trim();
  if (!text) {
    return null;
  }

  const username = String(userCore.screen_name ?? userLegacy.screen_name ?? "").trim();
  const authorSource = userCore.name ?? userLegacy.name ?? username;
  const author = String(authorSource || "Unknown");

  const likes = toNumber(legacy.favorite_count);
  const retweets = toNumber(legacy.retweet_count);
  const replies = toNumber(legacy.reply_count);
  const quotes = toNumber(legacy.quote_count);
  const followers = toNumber(userLegacy.followers_count);
  const verified = Boolean(user.is_blue_verified);

  return {
    id: String(tweet.rest_id ?? legacy.id_str ?? `${username}:${text.slice(0, 24)}`),
    text,
    author,
    username,
    author_image: String(asRecord(user.avatar).image_url ?? ""),
    verified,
    influencer: verified || followers >= 10_000,
    likes,
    retweets,
    followers,
    date: String(legacy.created_at ?? ""),
    sentiment: classifySentiment(text),
    engagement: likes + retweets + replies + quotes,
    hashtags: extractHashtags(legacy, text),
    trending: false,
  };
}

export function extractTwitterTimelineTweets(payload: unknown): TwitterTweet[] {
  const tweetResults: UnknownRecord[] = [];
  for (const instruction of getTimelineInstructions(payload)) {
    collectTweetResults(instruction, tweetResults);
  }

  const seen = new Set<string>();
  const normalized: TwitterTweet[] = [];

  for (const rawTweet of tweetResults) {
    const tweet = normalizeTweet(rawTweet);
    if (!tweet || seen.has(tweet.id)) {
      continue;
    }
    seen.add(tweet.id);
    normalized.push(tweet);
  }

  return normalized;
}

export function summarizeTwitterFeed(tweets: TwitterTweet[]): TwitterFeedSummary {
  const total = tweets.length;
  const bullish = tweets.filter((tweet) => tweet.sentiment === "Bullish").length;
  const bearish = tweets.filter((tweet) => tweet.sentiment === "Bearish").length;
  const neutralCount = total - bullish - bearish;

  const hashtagCounts = new Map<string, number>();
  for (const tweet of tweets) {
    for (const tag of tweet.hashtags) {
      hashtagCounts.set(tag, (hashtagCounts.get(tag) ?? 0) + 1);
    }
  }

  const influencerMap = new Map<string, TwitterInfluencer>();
  for (const tweet of tweets) {
    if (!tweet.username || !tweet.influencer) {
      continue;
    }
    const current = influencerMap.get(tweet.username);
    const candidate = {
      name: tweet.author || tweet.username,
      username: tweet.username,
      handle: tweet.username,
      followers: tweet.followers,
      verified: tweet.verified,
    };
    if (
      !current ||
      Number(candidate.verified) > Number(current.verified) ||
      candidate.followers > current.followers
    ) {
      influencerMap.set(tweet.username, candidate);
    }
  }

  const percentage = (count: number) =>
    total > 0 ? Math.round((count / total) * 1000) / 10 : 0;

  return {
    positive: percentage(bullish),
    negative: percentage(bearish),
    neutral: percentage(neutralCount),
    total,
    hashtags: [...hashtagCounts.entries()]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag)),
    influencers: [...influencerMap.values()].sort(
      (a, b) => Number(b.verified) - Number(a.verified) || b.followers - a.followers,
    ),
  };
}
